import { axiosClientAPI } from "@/lib/axios";
import { v2Api } from "@/lib/api/v2";
import {
  AuditLogEntry,
  AuthLoginInput,
  AuthRegisterInput,
  BulkDeleteRequest,
  BulkOperationResult,
  BulkQuestionCreateRequest,
  BulkQuizCreateRequest,
  BulkUserCreateRequest,
  ChangePasswordInput,
  CsvExportRequest,
  CsvImportRequest,
  LeaderboardEntry,
  Quiz,
  QuizAnalytics,
  QuizCreateInput,
  QuizPerformanceMetrics,
  QuizUpdateInput,
  RoomRun,
  RoomState,
  SystemAnalytics,
  SystemStats,
  UserAnalytics,
  UserManagementRow,
  UserRead,
  VerifyPasswordInput,
  VerifyPasswordResponse,
} from "@/types/api";

type Question = Quiz["questions"][number];

export { v2Api };
export type {
  ExerciseArchiveListItem,
  ExerciseArchiveListResponse,
  ImmutableExerciseReport,
  ImmutableExerciseArchivePayload,
  V2Paginated,
  ScenarioInject,
  ScenarioInjectPayload,
  InjectType,
  InjectOption,
  InjectRole,
  NewPhaseInline,
  NewTeamInline,
  Phase,
  PhasePayload,
} from "@/lib/api/v2";

async function apiRequest<T>(
  path: string,
  options: { method?: "GET" | "POST" | "PUT" | "DELETE"; data?: unknown; params?: Record<string, string | number> } = {},
): Promise<T | null> {
  const { method = "GET", data, params } = options;
  try {
    const response = await axiosClientAPI.request<T>({
      url: path,
      method,
      ...(data !== undefined && { data }),
      ...(params && Object.keys(params).length > 0 && { params }),
    });
    if (response.status === 204) return null;
    return response.data ?? null;
  } catch (error) {
    console.error(`[API] request failed for ${path}`, error);
    return null;
  }
}

export function getHealth() {
  return apiRequest<Record<string, unknown>>("/health");
}

export async function getQuizzes() {
  return (await apiRequest<Quiz[]>("/api/quizzes")) ?? [];
}

export async function getQuiz(id: string) {
  return apiRequest<Quiz>(`/api/quizzes/${id}`);
}

export async function createQuiz(payload: QuizCreateInput) {
  return apiRequest<Quiz>("/api/quizzes", { method: "POST", data: payload });
}

export async function updateQuiz(id: string, payload: QuizUpdateInput) {
  return apiRequest<Quiz>(`/api/quizzes/${id}`, { method: "PUT", data: payload });
}

export async function deleteQuiz(id: string) {
  await apiRequest<void>(`/api/quizzes/${id}`, { method: "DELETE" });
}

export async function getQuestions() {
  return (await apiRequest<Question[]>("/api/questions")) ?? [];
}

export async function createQuestion(payload: Question) {
  return apiRequest<Question>("/api/questions", { method: "POST", data: payload });
}

export async function updateQuestion(id: string, payload: Question) {
  return apiRequest<Question>(`/api/questions/${id}`, { method: "PUT", data: payload });
}

export async function deleteQuestion(id: string) {
  await apiRequest<void>(`/api/questions/${id}`, { method: "DELETE" });
}

export async function getRooms() {
  return (await apiRequest<RoomRun[]>("/api/rooms")) ?? [];
}

export async function getRoomState(roomCode: string) {
  return apiRequest<RoomState>(`/api/rooms/state/${roomCode}`);
}

export async function createRoom(payload: Partial<RoomRun>) {
  return apiRequest<RoomRun>("/api/rooms", { method: "POST", data: payload });
}

export async function updateRoom(id: string, payload: Partial<RoomRun>) {
  return apiRequest<RoomRun>(`/api/rooms/${id}`, { method: "PUT", data: payload });
}

export async function deleteRoom(id: string) {
  await apiRequest<void>(`/api/rooms/${id}`, { method: "DELETE" });
}

export async function getQuizAnalytics(quizId: string) {
  return apiRequest<QuizAnalytics>(`/api/analytics/quiz/${quizId}`);
}

export async function getUserAnalytics(userId: string) {
  return apiRequest<UserAnalytics>(`/api/analytics/user/${userId}`);
}

export async function getSystemAnalytics(
  params?: Record<string, string | number | undefined>,
) {
  const normalized: Record<string, string | number> | undefined = params
    ? (Object.fromEntries(
        Object.entries(params).filter(
          (entry): entry is [string, string | number] =>
            entry[1] !== undefined && entry[1] !== null,
        ),
      ) as Record<string, string | number>)
    : undefined;
  return (
    (await apiRequest<SystemAnalytics[]>("/api/analytics/system", {
      params: Object.keys(normalized ?? {}).length ? normalized : undefined,
    })) ?? []
  );
}

export async function getLeaderboard(limit?: number) {
  return (
    (await apiRequest<LeaderboardEntry[]>("/api/analytics/leaderboard", {
      params: limit !== undefined ? { limit } : undefined,
    })) ?? []
  );
}

export async function getQuizPerformance(limit?: number) {
  return (
    (await apiRequest<QuizPerformanceMetrics[]>(
      "/api/analytics/quiz-performance",
      { params: limit !== undefined ? { limit } : undefined },
    )) ?? []
  );
}

export async function generateSystemReport() {
  return apiRequest<SystemAnalytics>(
    "/api/analytics/generate-system-report",
    { method: "POST" },
  );
}

export async function bulkCreateUsers(payload: BulkUserCreateRequest) {
  return apiRequest<BulkOperationResult>("/api/bulk/users", {
    method: "POST",
    data: payload,
  });
}

export async function bulkCreateQuestions(payload: BulkQuestionCreateRequest) {
  return apiRequest<BulkOperationResult>("/api/bulk/questions", {
    method: "POST",
    data: payload,
  });
}

export async function bulkCreateQuizzes(payload: BulkQuizCreateRequest) {
  return apiRequest<BulkOperationResult>("/api/bulk/quizzes", {
    method: "POST",
    data: payload,
  });
}

export async function bulkDeleteEntities(
  entityType: string,
  payload: BulkDeleteRequest,
) {
  return apiRequest<BulkOperationResult>(`/api/bulk/${entityType}`, {
    method: "DELETE",
    data: payload,
  });
}

export async function importFromCsv(payload: CsvImportRequest) {
  return apiRequest<BulkOperationResult>("/api/bulk/import/csv", {
    method: "POST",
    data: payload,
  });
}

export async function exportToCsv(payload: CsvExportRequest) {
  return apiRequest<string>("/api/bulk/export/csv", {
    method: "POST",
    data: payload,
  });
}

export async function getCsvTemplate(entityType: string) {
  return apiRequest<string>(`/api/bulk/templates/csv/${entityType}`);
}

export async function getSystemStats() {
  return apiRequest<SystemStats>("/api/admin/stats");
}

export async function getUserManagement(params?: {
  skip?: number;
  limit?: number;
  search?: string;
}) {
  const p: Record<string, string | number> = {};
  if (params?.skip !== undefined) p.skip = params.skip;
  if (params?.limit !== undefined) p.limit = params.limit;
  if (params?.search) p.search = params.search;
  return (
    (await apiRequest<UserManagementRow[]>("/api/admin/users", {
      params: Object.keys(p).length ? p : undefined,
    })) ?? []
  );
}

export async function deactivateUser(userId: string) {
  return apiRequest<Record<string, string>>(
    `/api/admin/users/${userId}/deactivate`,
    { method: "POST" },
  );
}

export async function activateUser(userId: string) {
  return apiRequest<Record<string, string>>(
    `/api/admin/users/${userId}/activate`,
    { method: "POST" },
  );
}

export async function getSystemHealth() {
  return apiRequest<Record<string, unknown>>("/api/admin/health");
}

export async function getAuditLogs(params?: {
  skip?: number;
  limit?: number;
  user_id?: string;
  action?: string;
  start_date?: string;
  end_date?: string;
}) {
  const p: Record<string, string | number> | undefined = params
    ? (Object.fromEntries(
        Object.entries(params).filter(
          (entry): entry is [string, string | number] =>
            entry[1] !== undefined && entry[1] !== null && entry[1] !== "",
        ),
      ) as Record<string, string | number>)
    : undefined;
  return (
    (await apiRequest<AuditLogEntry[]>("/api/admin/audit-logs", {
      params: p && Object.keys(p).length ? p : undefined,
    })) ?? []
  );
}

export async function cleanupOldData(daysToKeep?: number) {
  return apiRequest<Record<string, number>>("/api/admin/cleanup", {
    method: "POST",
    params: daysToKeep !== undefined ? { days_to_keep: daysToKeep } : undefined,
  });
}

export async function toggleMaintenanceMode(enabled: boolean) {
  return apiRequest<Record<string, string>>("/api/admin/maintenance", {
    method: "POST",
    params: { enabled: String(enabled) },
  });
}

export async function registerUser(payload: AuthRegisterInput) {
  return apiRequest<UserRead>("/api/auth/register", {
    method: "POST",
    data: payload,
  });
}

export async function loginUser(payload: AuthLoginInput) {
  return apiRequest<UserRead>("/api/auth/login", {
    method: "POST",
    data: payload,
  });
}

export async function changePassword(payload: ChangePasswordInput) {
  return apiRequest<UserRead>("/api/auth/change-password", {
    method: "POST",
    data: payload,
  });
}

export async function verifyPassword(payload: VerifyPasswordInput) {
  return apiRequest<VerifyPasswordResponse>("/api/auth/verify-password", {
    method: "POST",
    data: payload,
  });
}

export interface ConsoleParticipant {
  /** ``ExerciseRunParticipantModel`` id for ``?participant=`` (relational console API). */
  id: string;
  user_id?: string;
  full_name?: string;
  role?: string;
  avatar?: string;
}

/** Flag data for console sidebar (matches IGroupedFlagData) */
export interface ConsoleFlagData {
  flag_id: string;
  question: string;
  score: number;
  team: string;
  participant_answers?: Array<{
    flag_id: string;
    submitted_response: string;
    obtained_score: number;
    hint_used: boolean;
    retires: number;
    updated_at: string;
    status: boolean;
  }>;
}

/** Current participant with flag_data for FLAG scenario console */
export interface ConsoleCurrentParticipant {
  id: string;
  user_id: string;
  full_name: string;
  avatar?: string;
  role?: string;
  team?: string;
  flag_data?: ConsoleFlagData[];
}

export interface ConsoleData {
  /** Relational exercise-run console API (GET /api/exercises/active/<id>/console) */
  active_scenario_id?: string;
  run_participant_id?: number;
  /** Full console URL from corporate console API (preferred) */
  console_url?: string;
  vnc_url?: string;
  vnc_host?: string;
  vnc_port?: number;
  token?: string;
  path?: string;
  /** Current participant id (corporate console API) */
  current_participant_id?: string;
  /** Current participant with flag_data (FLAG scenario only) */
  current_participant?: ConsoleCurrentParticipant;
  /** List of participants for switcher (corporate console API) */
  participants?: ConsoleParticipant[];
  /** Scenario type: FLAG | MILESTONE */
  scenario?: { name: string; type: string };
}

export async function getConsoleData(
  scenarioId: string,
  participantId?: string,
): Promise<ConsoleData | null> {
  const raw = participantId?.trim();
  const valid =
    raw &&
    raw !== 'undefined' &&
    raw !== 'null' &&
    raw !== 'NaN';
  return apiRequest<ConsoleData>(`/api/exercises/active/${scenarioId}/console`, {
    params: valid ? { participant: raw } : undefined,
  });
}

